import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DirtyProvider } from "./context/DirtyContext";
import { LangProvider } from "./context/LangContext";
import { ThemeProvider } from "./context/ThemeContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AccountTypePicker from "./pages/AccountTypePicker";
import EmployeeRegister from "./pages/EmployeeRegister";
import EmployeeLogin from "./pages/EmployeeLogin";
import EmployeeRecords, { EmployeeDailyList } from "./pages/EmployeeRecords";
import Ledger from "./pages/Ledger";
import ClientLedger from "./pages/ClientLedger";
import DailyMovement from "./pages/DailyMovement";
import AdvancedDailyMovement from "./pages/AdvancedDailyMovement";
import DailyVariantPicker from "./pages/DailyVariantPicker";
import SubscriptionGate from "./pages/SubscriptionGate";
import LedgerPicker, { RecordsPicker } from "./pages/LedgerPicker";
import History from "./pages/History";
import HistoryDetail from "./pages/HistoryDetail";
import Voucher from "./pages/Voucher";
import DiscountCustomers, { DiscountCustomerDetail } from "./pages/DiscountCustomers";
import PublicJoin from "./pages/PublicJoin";
import PublicCard from "./pages/PublicCard";
import DiscountMiniApp from "./pages/DiscountMiniApp";
import CarBookings from "./pages/CarBookings";
import LedgerSelection from "./pages/LedgerSelection";
import ManagerEmployeePerms from "./pages/ManagerEmployeePerms";
import Settings from "./pages/Settings";
import AdminAccounts from "./pages/AdminAccounts";
import AdminInvoices from "./pages/AdminInvoices";
import AdminChat from "./pages/AdminChat";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminDevices from "./pages/AdminDevices";
import Support from "./pages/Support";
import WhatsNew from "./pages/WhatsNew";
import SubscriptionPending from "./pages/SubscriptionPending";
import AccountLocked from "./pages/AccountLocked";
import MobileGate from "./components/MobileGate";
import ForgotPassword from "./pages/ForgotPassword";
import AccountRecovery from "./pages/AccountRecovery";
import TotpChallenge from "./pages/TotpChallenge";
import TotpSetup from "./pages/TotpSetup";
import OpeningBalances from "./pages/OpeningBalances";
import ShopLog from "./pages/ShopLog";
import ManagerEmployeeAccess from "./pages/ManagerEmployeeAccess";
import SplashScreen from "./components/SplashScreen";
import MShopMovement from "./pages/mshop/MShopMovement";
import MShopSetup from "./pages/mshop/MShopSetup";
import { MShopRecordsPage, MShopLogPage, MansoriShopsPage, MansoriShopDetailPage } from "./pages/mshop/MShopPages";
import "@/App.css";

/** Returns true when the owner has manually locked this account (or the
 *  employee's parent manager). Takes precedence over every other gate —
 *  user is bounced to /account-locked until the owner unlocks. */
function _isAccountLocked(company) {
    if (!company) return false;
    if (company.role === "admin") return false;
    return !!company.is_manually_locked;
}


/** Employees use their manager's subscription, not their own checkout. */
function _employeeSubscriptionBlocked(company) {
    return company?.account_type === "employee" &&
        ["none", "pending", "expired"].includes(company.subscription_status);
}

function EmployeeSubscriptionRoute({ children }) {
    const { company } = useAuth();
    if (company === null) return <div className="min-h-screen flex items-center justify-center" dir="rtl">جاري التحقق...</div>;
    if (company === false) return <Navigate to="/login" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (!_employeeSubscriptionBlocked(company)) return <Navigate to="/" replace />;
    return children;
}

function EmployeeSubscriptionExpired() {
    const { logout } = useAuth();
    return (
        <div className="min-h-screen flex items-center justify-center app-bg p-6" dir="rtl">
            <div className="max-w-md w-full rounded-2xl border bg-white p-8 text-center shadow-sm">
                <h1 className="text-2xl font-bold mb-4">انتهى الاشتراك</h1>
                <p className="text-neutral-600 mb-3">انتهى اشتراك المحل، يُرجى مراجعة المدير لتجديد الاشتراك.</p>
                <p className="text-sm text-neutral-500">جميع بيانات المحل محفوظة.</p>
                <button
                    type="button"
                    onClick={async () => {
                        try { await logout(); }
                        finally { window.location.replace("/login"); }
                    }}
                    className="mt-6 rounded-xl bg-neutral-900 px-6 py-3 text-white hover:bg-neutral-700"
                >
                    تسجيل الخروج
                </button>
            </div>
        </div>
    );
}

/** Returns true if the company should be forced through the /subscribe gate. */
function _needsSubscription(company) {
    if (!company) return false;
    if (company.role === "admin") return false;
    if (company.account_type === "employee") return false; // inherits from parent
    const st = company.subscription_status;
    return st === "none" || st === "expired";
}

/** Returns true when the company has a pending-approval subscription (either
 *  first-time or renewal with no active grace left). The user should land on
 *  the /subscribe/pending waiting screen rather than the ledger. */
function _needsPendingWait(company) {
    if (!company) return false;
    if (company.role === "admin") return false;
    if (company.account_type === "employee") return false;
    return company.subscription_status === "pending";
}

/** Returns true if the company is past the subscribe gate but hasn't picked
 *  their ledgers yet — they should land on /select-ledgers before anything
 *  else. Admins and employees are exempt (employees inherit from parent). */
function _needsLedgerSelection(company) {
    if (!company) return false;
    if (company.role === "admin") return false;
    if (company.account_type === "employee") return false;
    if (_needsSubscription(company)) return false;
    return !company.enabled_ledgers || company.enabled_ledgers.length === 0;
}

/** True when the user has picked their ledgers but hasn't yet chosen their
 *  daily-ledger flavour (standard/advanced) — push them to /daily/choose. */
function _needsDailyVariant(company) {
    if (!company) return false;
    if (company.role === "admin") return false;
    if (company.account_type === "employee") return false;
    if (_needsSubscription(company)) return false;
    if (_needsLedgerSelection(company)) return false;
    const enabled = company.enabled_ledgers || [];
    return enabled.includes("daily") && !company.daily_variant;
}

/** Post-variant onboarding: brand-new accounts capture their opening
 *  shop balances (gold weight + cash) BEFORE landing on the dashboard.
 *  `opening_captured` is back-filled to True by a startup migration for
 *  all pre-existing accounts so they skip this gate. Legacy accounts
 *  still get prompted — but only when they actually open /shop-log. */
function _needsOpeningBalances(company) {
    if (!company) return false;
    if (company.role === "admin") return false;
    if (company.account_type === "employee") return false;
    if (company.car_learn) return false;
    if (_needsSubscription(company)) return false;
    if (_needsLedgerSelection(company)) return false;
    if (_needsDailyVariant(company)) return false;
    // Opening gold/cash only matter for the daily-driven shop log.
    if (!(company.enabled_ledgers || []).includes("daily")) return false;
    return company.opening_captured !== true;
}

/** True when a brand-new manager has finished ledger + variant selection
 *  but hasn't yet declared employee-ledger permissions — push them to
 *  /manager/employee-perms once. */
function _needsManagerEmployeePerms(company) {
    if (company?.account_type !== "manager") return false;
    if (_needsSubscription(company)) return false;
    if (_needsLedgerSelection(company)) return false;
    if (_needsDailyVariant(company)) return false;
    // We treat `employees_extra_enabled` being explicitly `false` AND
    // `employees_extra_ledgers` being `null/undefined` as "never answered".
    // After they submit (Yes/No), the backend stores the answer and we
    // skip this gate forever. We add a hidden bit to track this.
    return company.employees_perms_answered !== true;
}

/** True when a brand-new manager has finished the employee-perms
 *  question but hasn't yet set `employee_limit` + `access_password`
 *  (or explicitly chose "Later"). This is the final onboarding step
 *  for managers — replaces the old flow that forced them to open
 *  Settings before they could receive employees. Legacy managers are
 *  back-filled `access_setup_answered=true` at server startup so they
 *  never see this gate. */
function _needsManagerAccessSetup(company) {
    if (company?.account_type !== "manager") return false;
    if (_needsSubscription(company)) return false;
    if (_needsLedgerSelection(company)) return false;
    if (_needsDailyVariant(company)) return false;
    if (_needsOpeningBalances(company)) return false;
    if (_needsManagerEmployeePerms(company)) return false;
    return company.access_setup_answered !== true;
}

function ProtectedRoute({ children }) {
    const { company } = useAuth();
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    // Mansori shop employees: only home tiles + settings (+ /mshop routes).
    if (company?.mansori_shop) {
        if (!company.mshop_setup_answered) return <Navigate to="/mshop/setup" replace />;
        if (path !== "/" && !path.startsWith("/settings")) return <Navigate to="/" replace />;
        return children;
    }
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (_needsDailyVariant(company)) return <Navigate to={
        company?.account_type === "manager" ? "/daily/choose?next=/manager/employee-perms" : "/daily/choose"
    } replace />;
    if (_needsOpeningBalances(company)) return <Navigate to="/onboarding/opening-balances" replace />;
    if (_needsManagerEmployeePerms(company)) return <Navigate to="/manager/employee-perms" replace />;
    if (_needsManagerAccessSetup(company)) return <Navigate to="/manager/employee-access" replace />;
    // Car Learn account is scoped strictly to /car-bookings + Settings; any
    // gold-system route (/, /client, /factory, /daily, /history, /voucher/*,
    // /discount-customers, /employees) is redirected back to the Car Learn
    // customer ledger. This only affects the car_learn flag; all other
    // accounts are untouched.
    if (company?.car_learn) {
        const isCarPath = path === "/car-bookings" || path.startsWith("/car-bookings/");
        const isAllowedShared = path.startsWith("/settings") || path === "/subscribe";
        if (!isCarPath && !isAllowedShared) return <Navigate to="/car-bookings" replace />;
    }
    return children;
}

/** Route wrapper for the onboarding step that CAPTURES the opening balances.
 *  Runs all earlier gates (subscription → variant) but NOT
 *  `_needsOpeningBalances` — since this is the page that resolves it —
 *  otherwise ProtectedRoute would send us here in an infinite Navigate loop
 *  (that's what causes the blank/blue screen after picking ledgers). */
function OpeningBalancesRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (company?.account_type === "employee") return <Navigate to="/" replace />;
    if (company?.car_learn) return <Navigate to="/car-bookings" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (_needsDailyVariant(company)) return <Navigate to="/daily/choose" replace />;
    // If the user already captured opening balances, they don't need this
    // page — bounce them home (which will then dispatch through the rest
    // of the ProtectedRoute chain, e.g. manager employee-perms).
    if (!_needsOpeningBalances(company)) return <Navigate to="/" replace />;
    return children;
}

function DailyEnabledRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (company?.car_learn) return <Navigate to="/car-bookings" replace />;
    if (company?.mansori_shop) return <Navigate to="/mshop" replace />;
    if (company?.ledger_mode === "basic") return <Navigate to="/" replace />;
    // Managers don't have a personal daily ledger.
    if (company?.account_type === "manager") return <Navigate to="/employees" replace />;
    if (_needsOpeningBalances(company)) return <Navigate to="/onboarding/opening-balances" replace />;
    return children;
}

/** Like DailyEnabledRoute but allows managers in — used only for the
 *  `/daily/choose` variant picker, which managers also need to pass through
 *  as part of the post-subscription onboarding. */
function DailyChooseRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (company?.mansori_shop) return <Navigate to="/" replace />;
    // Employees never pick the variant (manager decides); accounts without daily have nothing to pick.
    if (company?.account_type === "employee") return <Navigate to="/" replace />;
    if (!(company?.enabled_ledgers || []).includes("daily") || company?.ledger_mode === "basic") return <Navigate to="/" replace />;
    return children;
}

/** Switches between standard and advanced daily ledger based on
 *  the company's saved `daily_variant`. If unset, force user through
 *  the variant picker. */
function DailyDispatcher() {
    const { company } = useAuth();
    if (!company?.daily_variant) {
        return <Navigate to="/daily/choose" replace />;
    }
    return company.daily_variant === "advanced" ? <AdvancedDailyMovement /> : <DailyMovement />;
}

function ManagerRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (_needsDailyVariant(company)) return <Navigate to="/daily/choose?next=/manager/employee-perms" replace />;
    if (_needsOpeningBalances(company)) return <Navigate to="/onboarding/opening-balances" replace />;
    if (_needsManagerEmployeePerms(company)) return <Navigate to="/manager/employee-perms" replace />;
    if (_needsManagerAccessSetup(company)) return <Navigate to="/manager/employee-access" replace />;
    if (company?.account_type !== "manager") return <Navigate to="/" replace />;
    if (company?.mansori_manager && window.location.pathname.startsWith("/employees")) return <Navigate to="/" replace />;
    return children;
}

/** Same as ManagerRoute but without the employee-perms gate — used only by
 *  the /manager/employee-perms page itself to avoid an infinite redirect. */
function ManagerOnboardingRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (_needsDailyVariant(company)) return <Navigate to="/daily/choose?next=/manager/employee-perms" replace />;
    if (_needsOpeningBalances(company)) return <Navigate to="/onboarding/opening-balances" replace />;
    if (company?.account_type !== "manager") return <Navigate to="/" replace />;
    return children;
}

/** Route wrapper for the NEW /manager/employee-access onboarding step
 *  that captures `employee_limit` + `access_password`. Runs every earlier
 *  gate BUT skips `_needsManagerAccessSetup` (that's exactly what this
 *  page resolves) — otherwise we'd loop forever. */
function ManagerAccessSetupRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (_needsDailyVariant(company)) return <Navigate to="/daily/choose?next=/manager/employee-access" replace />;
    if (_needsOpeningBalances(company)) return <Navigate to="/onboarding/opening-balances" replace />;
    if (_needsManagerEmployeePerms(company)) return <Navigate to="/manager/employee-perms" replace />;
    if (company?.account_type !== "manager") return <Navigate to="/" replace />;
    // If the manager already answered this step, bounce them to /employees
    // so they don't see the onboarding page again by typing the URL.
    if (!_needsManagerAccessSetup(company)) return <Navigate to="/employees" replace />;
    return children;
}

function NonEmployeeRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    if (_needsLedgerSelection(company)) return <Navigate to="/select-ledgers" replace />;
    if (_needsDailyVariant(company)) return <Navigate to="/daily/choose" replace />;
    if (_needsOpeningBalances(company)) return <Navigate to="/onboarding/opening-balances" replace />;
    if (_needsManagerEmployeePerms(company)) return <Navigate to="/manager/employee-perms" replace />;
    if (_needsManagerAccessSetup(company)) return <Navigate to="/manager/employee-access" replace />;
    // Car Learn: gold ledgers (client/factory/vouchers) are not part of this
    // account — always bounce back to the car-bookings customer ledger.
    if (company?.car_learn) return <Navigate to="/car-bookings" replace />;
    if (company?.mansori_shop) {
        const p = window.location.pathname;
        return <Navigate to={p.startsWith("/history") ? "/mshop/records" : "/"} replace />;
    }
    // Employees: only their own daily records (read via /history/daily); everything else bounces.
    if (company?.account_type === "employee") {
        const p = window.location.pathname;
        const hasDaily = company.ledger_mode !== "basic";
        if (hasDaily && (p === "/history/daily" || p.startsWith("/history/daily/"))) return children;
        if (hasDaily && p === "/history") return <Navigate to="/history/daily" replace />;
        return <Navigate to={hasDaily ? "/daily" : "/"} replace />;
    }
    return children;
}

/** Mansori shop employee pages (sale/purchase ledger, records, shop log). */
function MShopRoute({ children, setup = false }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (!company?.mansori_shop) return <Navigate to="/" replace />;
    if (setup && company.mshop_setup_answered) return <Navigate to="/" replace />;
    if (!setup && !company.mshop_setup_answered) return <Navigate to="/mshop/setup" replace />;
    return children;
}

/** Mansori manager: per-employee shop list. */
function MansoriManagerRoute({ children }) {
    const { company } = useAuth();
    if (company && !company.mansori_manager) return <Navigate to="/" replace />;
    return <ManagerRoute>{children}</ManagerRoute>;
}

function AdminRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role !== "admin") return <Navigate to="/" replace />;
    return children;
}

function PublicOnly({ children }) {
    const { company } = useAuth();
    if (company && company !== false) {
        if (company.role === "admin") return <Navigate to="/admin/accounts" replace />;
        if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
        return <Navigate to="/" replace />;
    }
    return children;
}

/** Allow access to /subscribe only when user is logged-in AND actually needs to subscribe.
 *  Logged-out → /login. Already-subscribed → /. Admin → admin dashboard. */
function SubscribeRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    // Employees inherit; if they reach /subscribe somehow, send them home.
    if (company?.account_type === "employee") return <Navigate to="/" replace />;
    if (!_needsSubscription(company)) return <Navigate to="/" replace />;
    return children;
}

function LedgerSelectionRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    if (company?.account_type === "employee") return <Navigate to="/" replace />;
    if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
    if (_needsPendingWait(company)) return <Navigate to="/subscribe/pending" replace />;
    // Already picked their ledgers — skip back to home.
    if (!_needsLedgerSelection(company)) return <Navigate to="/" replace />;
    return children;
}

/** Waiting screen while subscription is pending-approval. Only resolves
 *  the pending-wait gate — doesn't force ledger/onboarding checks since
 *  the user has no ledger access until activated. */
function SubscriptionPendingRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (company?.role === "admin") return <Navigate to="/admin/accounts" replace />;
    // If the user is no longer pending (approved OR fully expired), route
    // them appropriately so the waiting screen doesn't trap them.
    if (!_needsPendingWait(company)) {
        if (_isAccountLocked(company)) return <Navigate to="/account-locked" replace />;
    if (_employeeSubscriptionBlocked(company)) return <Navigate to="/employee/subscription-expired" replace />;
    if (_needsSubscription(company)) return <Navigate to="/subscribe" replace />;
        return <Navigate to="/" replace />;
    }
    return children;
}

/** Shows the "تم إقفال حسابك" screen only when the backend reports
 *  is_manually_locked=true. If the owner unlocks while the user is on
 *  this page, bounce them home automatically. */
function AccountLockedRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    if (!_isAccountLocked(company)) return <Navigate to="/" replace />;
    return children;
}

/** Minimal auth-only gate used by /support and /whats-new — these pages
 *  are accessible from ANY authed state (even subscription=none/pending
 *  or admin), so we skip all the onboarding/subscription gates. */
function AnyAuthedRoute({ children }) {
    const { company } = useAuth();
    if (company === null) {
        return (
            <div className="flex min-h-screen items-center justify-center app-bg" dir="rtl">
                <div className="text-sm text-neutral-500">جاري التحقق...</div>
            </div>
        );
    }
    if (company === false) return <Navigate to="/login" replace />;
    return children;
}

function AppShellRoutes() {
    return (
        <BrowserRouter>
            <DirtyProvider>
                <MobileGate>
                <Routes>
                    <Route
                        path="/login"
                        element={
                            <PublicOnly>
                                <Login />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            <PublicOnly>
                                <Register />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/register/type"
                        element={
                            <PublicOnly>
                                <AccountTypePicker />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/register/employee"
                        element={
                            <PublicOnly>
                                <EmployeeRegister />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/employee/login"
                        element={
                            <PublicOnly>
                                <EmployeeLogin />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/forgot"
                        element={
                            <PublicOnly>
                                <ForgotPassword />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/recover"
                        element={
                            <PublicOnly>
                                <AccountRecovery />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/login/totp"
                        element={
                            <PublicOnly>
                                <TotpChallenge />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/login/totp/setup"
                        element={
                            <PublicOnly>
                                <TotpSetup />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/employee/subscription-expired"
                        element={
                            <EmployeeSubscriptionRoute>
                                <EmployeeSubscriptionExpired />
                            </EmployeeSubscriptionRoute>
                        }
                    />
                    <Route
                        path="/subscribe"
                        element={
                            <SubscribeRoute>
                                <SubscriptionGate />
                            </SubscribeRoute>
                        }
                    />
                    <Route
                        path="/select-ledgers"
                        element={
                            <LedgerSelectionRoute>
                                <LedgerSelection />
                            </LedgerSelectionRoute>
                        }
                    />
                    <Route
                        path="/manager/employee-perms"
                        element={
                            <ManagerOnboardingRoute>
                                <ManagerEmployeePerms />
                            </ManagerOnboardingRoute>
                        }
                    />
                    <Route
                        path="/manager/employee-access"
                        element={
                            <ManagerAccessSetupRoute>
                                <ManagerEmployeeAccess />
                            </ManagerAccessSetupRoute>
                        }
                    />
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <LedgerPicker />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/factory"
                        element={
                            <NonEmployeeRoute>
                                <Ledger />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/client"
                        element={
                            <NonEmployeeRoute>
                                <ClientLedger />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/daily"
                        element={
                            <DailyEnabledRoute>
                                <DailyDispatcher />
                            </DailyEnabledRoute>
                        }
                    />
                    <Route
                        path="/daily/choose"
                        element={
                            <DailyChooseRoute>
                                <DailyVariantPicker />
                            </DailyChooseRoute>
                        }
                    />
                    <Route
                        path="/voucher/:type"
                        element={
                            <NonEmployeeRoute>
                                <Voucher />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/employees"
                        element={
                            <ManagerRoute>
                                <EmployeeRecords />
                            </ManagerRoute>
                        }
                    />
                    <Route
                        path="/employees/:empId"
                        element={
                            <ManagerRoute>
                                <EmployeeDailyList />
                            </ManagerRoute>
                        }
                    />
                    <Route
                        path="/employees/:empId/daily/:id"
                        element={
                            <ManagerRoute>
                                <HistoryDetail />
                            </ManagerRoute>
                        }
                    />
                    <Route
                        path="/history"
                        element={
                            <NonEmployeeRoute>
                                <RecordsPicker />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/history/:kind"
                        element={
                            <NonEmployeeRoute>
                                <History />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/history/:kind/:id"
                        element={
                            <NonEmployeeRoute>
                                <HistoryDetail />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/discount-customers"
                        element={
                            <NonEmployeeRoute>
                                <DiscountCustomers />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/discount-customers/:id"
                        element={
                            <NonEmployeeRoute>
                                <DiscountCustomerDetail />
                            </NonEmployeeRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <Settings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/accounts"
                        element={
                            <AdminRoute>
                                <AdminAccounts />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/invoices"
                        element={
                            <AdminRoute>
                                <AdminInvoices />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/chat"
                        element={
                            <AdminRoute>
                                <AdminChat />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/announcements"
                        element={
                            <AdminRoute>
                                <AdminAnnouncements />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/pending-subs"
                        element={<Navigate to="/admin/accounts" replace />}
                    />
                    <Route
                        path="/admin/devices"
                        element={
                            <AdminRoute>
                                <AdminDevices />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/subscribe/pending"
                        element={
                            <SubscriptionPendingRoute>
                                <SubscriptionPending />
                            </SubscriptionPendingRoute>
                        }
                    />
                    <Route
                        path="/account-locked"
                        element={
                            <AccountLockedRoute>
                                <AccountLocked />
                            </AccountLockedRoute>
                        }
                    />
                    <Route
                        path="/support"
                        element={
                            <AnyAuthedRoute>
                                <Support />
                            </AnyAuthedRoute>
                        }
                    />
                    <Route
                        path="/whats-new"
                        element={
                            <AnyAuthedRoute>
                                <WhatsNew />
                            </AnyAuthedRoute>
                        }
                    />
                    <Route path="/d/:shop" element={<DiscountMiniApp />} />
                    <Route
                        path="/car-bookings"
                        element={
                            <ProtectedRoute>
                                <CarBookings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/car-bookings/records"
                        element={
                            <ProtectedRoute>
                                <CarBookings mode="records" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/onboarding/opening-balances"
                        element={
                            <OpeningBalancesRoute>
                                <OpeningBalances />
                            </OpeningBalancesRoute>
                        }
                    />
                    <Route
                        path="/shop-log"
                        element={
                            <ProtectedRoute>
                                <ShopLog />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/mshop" element={<MShopRoute><MShopMovement /></MShopRoute>} />
                    <Route path="/mshop/setup" element={<MShopRoute setup><MShopSetup /></MShopRoute>} />
                    <Route path="/mshop/records" element={<MShopRoute><MShopRecordsPage /></MShopRoute>} />
                    <Route path="/mshop/shop-log" element={<MShopRoute><MShopLogPage /></MShopRoute>} />
                    <Route path="/mshop/shops" element={<MansoriManagerRoute><MansoriShopsPage /></MansoriManagerRoute>} />
                    <Route path="/mshop/shops/:empId" element={<MansoriManagerRoute><MansoriShopDetailPage /></MansoriManagerRoute>} />
                    <Route path="/join/:shop" element={<PublicJoin />} />
                    <Route path="/card/:customer_id" element={<PublicCard />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </MobileGate>
            </DirtyProvider>
        </BrowserRouter>
    );
}

export default function App() {
    const [splashDone, setSplashDone] = useState(false);
    // Public loyalty pages (/join/* and /card/*) skip the Meyar splash so
    // external customers never see internal branding.
    const isPublicPage = typeof window !== "undefined" && /^\/(join|card|d)(\/|$)/.test(window.location.pathname);

    // Hide the static boot-splash from index.html as soon as React mounts.
    useEffect(() => {
        const node = document.getElementById("boot-splash");
        if (node) node.classList.add("hide");
        const t = setTimeout(() => node?.parentNode?.removeChild(node), 500);
        return () => clearTimeout(t);
    }, []);

    return (
        <div className="App">
            <ThemeProvider>
                <LangProvider>
                    <AuthProvider>
                        <AppShellRoutes />
                        <Toaster richColors position="top-center" />
                    </AuthProvider>
                    {!isPublicPage && !splashDone ? <SplashScreen onDone={() => setSplashDone(true)} /> : null}
                </LangProvider>
            </ThemeProvider>
        </div>
    );
}
